// src/components/Gamification/XPBar.jsx
import React, { useEffect, useState } from 'react';

/**
 * XP progress bar su level up animacija.
 * Props:
 *   xp             – bendras XP
 *   level          – dabartinis lygis
 *   xpInLevel      – XP dabartiniame lygyje
 *   xpToNextLevel  – XP iki kito lygio
 *   progressPct    – % (0–100)
 *   levelUp        – ar tik ką kilo lygis (bool)
 *   compact        – mažas variantas header'ui
 */
export default function XPBar({
  xp = 0,
  level = 1,
  xpInLevel = 0,
  xpToNextLevel = 100,
  progressPct = 0,
  levelUp = false,
  compact = false,
}) {
  const [showLevelUp, setShowLevelUp] = useState(false);
  const [displayPct, setDisplayPct] = useState(0);

  useEffect(() => {
    // Animuotas bar užpildymas
    const t = setTimeout(() => setDisplayPct(progressPct), 100);
    return () => clearTimeout(t);
  }, [progressPct]);

  useEffect(() => {
    if (levelUp) {
      setShowLevelUp(true);
      const t = setTimeout(() => setShowLevelUp(false), 3000);
      return () => clearTimeout(t);
    }
  }, [levelUp]);

  if (compact) {
    return (
      <div className="flex items-center gap-2.5 min-w-[140px]">
        {/* Level badge */}
        <div
          className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-black text-white flex-shrink-0 relative"
          style={{ 
            background: 'linear-gradient(135deg, #58CC02, #46A302)',
            borderBottom: '3px solid #3A8702'
          }}
        >
          <span className="-mt-0.5">{level}</span>
        </div>
        {/* Bar */}
        <div className="flex-1 relative top-0.5">
          <div className="h-5 rounded-full bg-gray-200 shadow-inner relative" style={{ borderBottom: '3px solid #E5E7EB' }}>
            <div
              className="h-full rounded-full relative overflow-hidden"
              style={{ 
                width: `${displayPct}%`, 
                transition: 'width 0.8s cubic-bezier(0.34,1.56,0.64,1)',
                background: 'linear-gradient(180deg, #FFD900 0%, #FFB000 100%)',
                borderRight: displayPct > 0 ? '2px solid #E69F00' : 'none'
              }}
            >
              {/* Glossy inner highlight */}
              <div className="absolute top-0.5 left-1.5 right-1.5 h-1.5 bg-white opacity-40 rounded-full" />
            </div>
          </div>
          <div className="flex justify-between items-center mt-1 px-1">
            <span className="text-[10px] items-center font-black text-gray-400 uppercase tracking-widest">Level {level}</span>
            <p className="text-[11px] text-amber-500 font-black">
              {xpInLevel} / {xpToNextLevel} XP
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Level Up Popup */}
      {showLevelUp && (
        <div className="absolute -top-12 left-1/2 -translate-x-1/2 z-50 animate-level-up pointer-events-none">
          <div
            className="px-4 py-2 rounded-full text-white font-black text-sm whitespace-nowrap shadow-xl"
            style={{ background: 'linear-gradient(135deg, #FFC800, #FF9600)' }}
          >
            🎉 LYGIS {level}! Sveikiname!
          </div>
        </div>
      )}

      <div className="flex items-center gap-3">
        {/* Level badge */}
        <div
          className="w-11 h-11 rounded-full flex items-center justify-center font-black text-white text-sm shadow-md flex-shrink-0"
          style={{ background: 'linear-gradient(135deg, #58CC02, #46A302)', boxShadow: '0 4px 12px rgba(88,204,2,0.4)' }}
        >
          {level}
        </div>

        {/* Bar + labels */}
        <div className="flex-1">
          <div className="flex justify-between text-xs font-bold text-gray-500 mb-1">
            <span>Level {level}</span>
            <span>{xpInLevel} / {xpToNextLevel} XP</span>
          </div>
          <div className="xp-bar-track">
            <div
              className="xp-bar-fill"
              style={{ width: `${displayPct}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
